import { useState, useMemo } from "react"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material"
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as BankIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import { useNavigate } from "react-router-dom"
import ReportGenerator from "./ReportGenerator"

function StatCard({ title, value, icon, color = "primary", subtitle, loading = false }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Typography variant="h4" component="h2">
                  {value}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" color="textSecondary">
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  )
}

function FeeCollectorDashboard() {
  const navigate = useNavigate()
  const [dateFilter, setDateFilter] = useState("today") // "today", "30days", "month", "all"

  // Add state for report dialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  // Fetch all receipts
  const { data: receipts, isLoading: receiptsLoading, error: receiptsError, refetch } = useApiQuery(
    ["receipts"],
    "/receipts"
  )

  // Fetch all jobs with pending fees
  // const { data: pendingJobs, isLoading: jobsLoading } = useApiQuery(
  //   ["pending-fee-jobs"],
  //   "/jobs/pending-fees"
  // )

  // Helper function to filter receipts by date
  const getFilteredReceipts = useMemo(() => {
    if (!receipts) return []

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

    return receipts.filter((receipt) => {
      const receiptDate = new Date(receipt.collectedDate)

      switch (dateFilter) {
        case "today":
          return receiptDate >= today
        case "30days":
          return receiptDate >= thirtyDaysAgo
        case "month":
          return receiptDate >= monthStart
        case "all":
        default:
          return true
      }
    })
  }, [receipts, dateFilter])

  // Calculate statistics from filtered receipts
  const stats = useMemo(() => {
    const filtered = getFilteredReceipts

    const totalCollected = filtered.reduce((sum, receipt) => sum + (receipt.amount || 0), 0)
    const receiptsCount = filtered.length

    // Calculate pending collection from jobs
    // const pendingCollection = (pendingJobs || []).reduce((sum, job) => {
    //   const totalFee = job.fees || 0
    //   const paid = job.totalFeesPaid || 0
    //   return sum + (totalFee - paid)
    // }, 0)

    return {
      totalCollected,
      // pendingCollection,
      receiptsAdded: receiptsCount,
    }
  }, [getFilteredReceipts])

  // NEW: Calculate payment method summary
  const paymentMethodSummary = useMemo(() => {
  const filtered = getFilteredReceipts;
  const summary = {};

  filtered.forEach((receipt) => {
    const method = receipt.paymentMethod?.toLowerCase() || "unknown";

    if (!summary[method]) {
      summary[method] = {
        count: 0,
        total: 0,
        receipts: [],
        banks: {}  // NEW: store bank breakdown
      };
    }

    summary[method].count += 1;
    summary[method].total += receipt.amount || 0;
    summary[method].receipts.push(receipt);

    // NEW: If it is a bank transfer → group by bank name
    if (method === "bank_transfer") {
      const bankName = receipt.bank || "Unknown Bank";

      if (!summary[method].banks[bankName]) {
        summary[method].banks[bankName] = {
          count: 0,
          total: 0,
        };
      }

      summary[method].banks[bankName].count += 1;
      summary[method].banks[bankName].total += receipt.amount || 0;
    }
  });

  return Object.entries(summary)
    .map(([method, data]) => ({
      method,
      ...data,
    }))
    .sort((a, b) => b.total - a.total);
}, [getFilteredReceipts]);


  // Get recent receipts (last 10)
  const recentReceipts = useMemo(() => {
    return [...getFilteredReceipts]
      .sort((a, b) => new Date(b.collectedDate).getTime() - new Date(a.collectedDate).getTime())
      .slice(0, 10)
  }, [getFilteredReceipts])

  const getStatusColor = (status) => {
    const statusColors = {
      Verified: "success",
      "Pending Verification": "warning",
      Rejected: "error",
      completed: "success",
      processing: "primary",
      new: "info",
    }
    return statusColors[status] || "default"
  }

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      cash: "primary",
      "bank transfer": "success",
      cheque: "info",
      upi: "secondary",
      card: "warning",
    }
    return methodColors[method?.toLowerCase()] || "default"
  }

  // NEW: Get icon for payment method
  const getPaymentMethodIcon = (method) => {
    const lowerMethod = method?.toLowerCase()
    switch (lowerMethod) {
      case "cash":
        return <MoneyIcon />
      case "bank transfer":
        return <BankIcon />
      case "cheque":
        return <ReceiptIcon />
      case "upi":
        return <PaymentIcon />
      case "card":
        return <PaymentIcon />
      default:
        return <PaymentIcon />
    }
  }

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  // Helper to get client name
  const getClientName = (receipt) => {
    if (typeof receipt.clientId === "object" && receipt.clientId?.name) {
      return receipt.clientId.name
    }
    return receipt.clientName || "Unknown Client"
  }

  if (receiptsLoading ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (receiptsError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading dashboard data: {receiptsError.message}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Fee Collection Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage fee receipts and track collections.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh Data">
            <IconButton onClick={() => refetch()} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/add-fee-receipts")}
            size="large"
            sx={{
              backgroundColor: "#fcd34d",
              color: "#000",
              "&:hover": {
                backgroundColor: "#fbbf24",
              },
            }}
          >
            Add Fee Receipt
          </Button>
        </Stack>
      </Box>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CalendarIcon color="action" />
          <Typography variant="body1" fontWeight={600}>
            Filter Period:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Time Period</InputLabel>
            <Select value={dateFilter} label="Time Period" onChange={(e) => setDateFilter(e.target.value)}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            Showing data for: <strong>{getFilterLabel()}</strong>
          </Typography>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Collected"
            value={`₹${stats.totalCollected.toLocaleString()}`}
            icon={<MoneyIcon sx={{ fontSize: 40 }} />}
            color="success"
            subtitle={getFilterLabel()}
          />
        </Grid>
        {/* <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pending Collection"
            value={`₹${stats.pendingCollection.toLocaleString()}`}
            icon={<TrendingUpIcon sx={{ fontSize: 40 }} />}
            color="warning"
            subtitle="Outstanding amount"
          />
        </Grid> */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Receipts Added"
            value={stats.receiptsAdded}
            icon={<ReceiptIcon sx={{ fontSize: 40 }} />}
            color="primary"
            subtitle={getFilterLabel()}
          />
        </Grid>
      </Grid>

      {/* NEW: Payment Method Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PaymentIcon />
              Collection Summary by Payment Method
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {paymentMethodSummary.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  No payment data available for the selected period.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {paymentMethodSummary.map((summary) => (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={summary.method}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: 3,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="overline" color="textSecondary" fontWeight={600}>
                              {summary.method.toUpperCase()}
                            </Typography>
                            <Box sx={{ color: `${getPaymentMethodColor(summary.method)}.main` }}>
                              {getPaymentMethodIcon(summary.method)}
                            </Box>
                          </Box>

                          <Typography variant="h5" fontWeight="bold" color="primary.main">
                            ₹{summary.total.toLocaleString()}
                          </Typography>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={`${summary.count} receipt${summary.count !== 1 ? "s" : ""}`}
                              size="small"
                              color={getPaymentMethodColor(summary.method)}
                              variant="outlined"
                            />
                          </Box>
                          {summary.method === "bank_transfer" && (
                            <Box sx={{ mt: 1 }}>
                              {Object.entries(summary.banks).map(([bank, info]) => (
                                <Typography key={bank} variant="caption" color="textSecondary" display="block">
                                  {bank}: {info.count} tx • ₹{info.total.toLocaleString()}
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {/* <Typography variant="caption" color="textSecondary">
                            Avg: ₹{Math.round(summary.total / summary.count).toLocaleString()}
                          </Typography> */}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Total Summary Card */}
                <Grid item xs={12} sm={6} md={4} lg={2.4}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      bgcolor: "primary.main",
                      color: "white",
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography variant="overline" fontWeight={600}>
                            TOTAL
                          </Typography>
                          <TrendingUpIcon />
                        </Box>

                        <Typography variant="h5" fontWeight="bold">
                          ₹{stats.totalCollected.toLocaleString()}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={`${stats.receiptsAdded} total`}
                            size="small"
                            sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                          />
                        </Box>

                        <Typography variant="caption">
                          {paymentMethodSummary.length} payment method{paymentMethodSummary.length !== 1 ? "s" : ""}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Receipts */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Recent Fee Receipts</Typography>
              <Button size="small" variant="outlined" onClick={() => navigate("/all-receipts")}>
                View All
              </Button>
            </Box>

            {recentReceipts.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <ReceiptIcon sx={{ fontSize: 60, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No receipts found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {dateFilter !== "all"
                    ? `No receipts for ${getFilterLabel().toLowerCase()}. Try changing the filter.`
                    : "Start by adding your first fee receipt."}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/add-fee-receipts")}
                  sx={{ mt: 2 }}
                >
                  Add Fee Receipt
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Receipt No.</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>Date</TableCell>
                      {/* <TableCell>Job Status</TableCell> */}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentReceipts.map((receipt) => (
                      <TableRow key={receipt._id || receipt.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {receipt.receiptNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {getClientName(receipt)}
                          </Typography>
                          {/* <Typography variant="caption" color="textSecondary">
                            Job: {getJobId(receipt)}
                          </Typography> */}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            ₹{(receipt.amount || 0).toLocaleString()}
                          </Typography>
                          {receipt.amountDue !== undefined && (
                            <Typography variant="caption" color="textSecondary">
                              Due: ₹{receipt.amountDue.toLocaleString()}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={receipt.paymentMethod?.toUpperCase()}
                            color={getPaymentMethodColor(receipt.paymentMethod)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {receipt.bank ? (
                            <Chip
                              label={receipt.bank}
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(receipt.collectedDate).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(receipt.collectedDate).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Typography>
                        </TableCell>
                        {/* <TableCell>
                          <Chip
                            label={receipt.jobStatus?.toUpperCase() || "N/A"}
                            color={getStatusColor(receipt.jobStatus)}
                            size="small"
                          />
                        </TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                    transition: "all 0.2s",
                  }}
                  onClick={() => navigate("/all-receipts")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <ReceiptIcon sx={{ fontSize: 40, color: "secondary.main", mb: 1 }} />
                    <Typography variant="h6">View All Receipts</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Browse receipt history
                    </Typography>
                    <Chip label={`${receipts?.length || 0} Total`} size="small" color="secondary" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                    transition: "all 0.2s",
                  }}
                  onClick={() => navigate("/add-fee-receipts")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <AddIcon sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
                    <Typography variant="h6">Add Receipt</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Record new payment
                    </Typography>
                    <Chip label="Quick Add" size="small" color="primary" sx={{ mt: 1 }} />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                {/* <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                    transition: "all 0.2s",
                  }}
                  onClick={() => navigate("/all-jobs")}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <MoneyIcon sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
                    <Typography variant="h6">Pending Collections</Typography>
                    <Typography variant="body2" color="textSecondary">
                      View outstanding fees
                    </Typography>
                    <Chip label={`${pendingJobs?.length || 0} Jobs`} size="small" color="warning" sx={{ mt: 1 }} />
                  </CardContent>
                </Card> */}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover", transform: "translateY(-2px)" },
                    transition: "all 0.2s",
                  }}
                  onClick={() => setReportDialogOpen(true)}
                >
                  <CardContent sx={{ textAlign: "center" }}>
                    <CalendarIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Collection Report
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Generate Fee Collection Reports
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {/* Report Generator Dialog - place it here (outside the Grid) */}
      <ReportGenerator open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />
    </Box>
  )
}

export default FeeCollectorDashboard